import React from 'react';
import type { Organization, UserProfile } from '@types';
import { openCheckout, getPaddleTierPriceIds } from '../../services/paddleService';
import { getOrganizationDisplayName } from '@common/organizationDisplayName';

interface InactiveAccountViewProps {
    organization: Organization;
    currentUser: UserProfile;
    onLogout: () => void;
}

const InactiveAccountView: React.FC<InactiveAccountViewProps> = ({ organization, currentUser, onLogout }) => {
    const [isProcessing, setIsProcessing] = React.useState(false);

    const deactivatedDate = organization.deactivatedAt ? new Date(organization.deactivatedAt) : new Date();
    const deletionDate = new Date(deactivatedDate);
    deletionDate.setDate(deletionDate.getDate() + 30);
    
    const now = new Date();
    const diffTime = deletionDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    const daysRemaining = Math.max(0, diffDays);

    const handleReactivate = async () => {
        setIsProcessing(true);
        try {
            // Default to business plan for re-activation
            const priceId = (await getPaddleTierPriceIds()).business;
            
            await openCheckout({
                items: [{ priceId, quantity: 1 }],
                customer: { email: currentUser.email },
                customData: {
                    organization_id: organization.id,
                    user_id: currentUser.uid,
                    plan_type: 'business'
                },
                settings: {
                    displayMode: 'overlay',
                    theme: 'dark',
                    showAddDiscounts: true
                }
            });
        } catch (err) {
            console.error("Reactivation failed:", err);
            setIsProcessing(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center py-12 px-4 animate-fade-in relative overflow-hidden">
            {/* Background ambient glows */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none opacity-20">
                <div className="absolute top-20 left-20 w-72 h-72 bg-red-600 rounded-full blur-[120px]"></div>
                <div className="absolute bottom-20 right-20 w-96 h-96 bg-orange-600 rounded-full blur-[150px]"></div>
            </div>

            <div className="max-w-2xl w-full relative z-10 text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-[11px] font-black uppercase tracking-[0.2em] mb-8 animate-pulse">
                    <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                    </span>
                    Account Inactive - Grace Period Active
                </div>

                <h1 className="text-5xl md:text-6xl font-black text-white mb-8 tracking-tighter">
                    Subscription <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">Suspended</span>
                </h1>

                <div className="bg-slate-900/40 backdrop-blur-xl rounded-[2.5rem] border border-red-500/20 p-10 md:p-12 mb-10 shadow-2xl relative overflow-hidden group">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-500/50 to-transparent opacity-50"></div>
                    
                    <p className="text-xl text-slate-300 leading-relaxed mb-8">
                        The subscription for <span className="text-white font-bold">{getOrganizationDisplayName(organization)}</span> has been canceled.
                        Your data is currently preserved, but will be <span className="text-red-400 font-bold uppercase underline decoration-2 underline-offset-4">permanently deleted</span> in:
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                        <div className="bg-slate-950/50 p-6 rounded-2xl border border-slate-800 flex flex-col items-center justify-center">
                            <span className="text-4xl font-black text-white mb-1">{daysRemaining}</span>
                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Days Remaining</span>
                        </div>
                        <div className="md:col-span-2 bg-gradient-to-br from-red-500/5 to-orange-500/5 p-6 rounded-2xl border border-red-500/10 flex flex-col justify-center text-left">
                            <div className="flex items-center gap-3 mb-2 text-red-400">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
                                <span className="text-xs font-black uppercase tracking-widest">Wipe Warning</span>
                            </div>
                            <p className="text-xs text-slate-400 font-bold leading-relaxed">
                                Beyond this window, all intelligence frameworks, saved reports, and user access will be irrevocably purged.
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-5">
                        <button 
                            onClick={onLogout}
                            className="px-8 py-5 rounded-2xl font-bold text-slate-400 hover:text-white hover:bg-slate-800 transition-all border border-slate-800 flex-1 uppercase tracking-widest text-xs"
                        >
                            Log Out
                        </button>
                        <button 
                            onClick={handleReactivate}
                            disabled={isProcessing}
                            className="px-10 py-5 rounded-2xl font-black text-white transition-all bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 shadow-[0_10px_30px_rgba(59,130,246,0.3)] hover:shadow-blue-500/40 disabled:opacity-50 flex-[2] flex items-center justify-center gap-3 uppercase tracking-widest text-xs"
                        >
                            {isProcessing ? (
                                <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                            ) : (
                                <>
                                    Re-activate Intelligence Suite
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 7l5 5m0 0l-5 5m5-5H6"/></svg>
                                </>
                            )}
                        </button>
                    </div>
                </div>

                <p className="text-[10px] font-bold text-slate-600 uppercase tracking-[0.4em]">
                    IntelOptics Resiliency Guard — Automated Lifecycle Protocol
                </p>
            </div>
        </div>
    );
};

export default InactiveAccountView;
